import React, { ReactElement } from "react";

import { Stack, Typography } from "@mui/material";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";

import CircuitBreakerThresholdsSetting from "../../../../components/CircuitBreakerThresholdsSetting";
import BasicSelector from "../../../../components/Redux/BasicSelector";
import KeyPairPicker from "../../../../components/Redux/KeyPairPicker";
import OptionalStringList from "../../../../components/Redux/OptionalStringList";
import Switch from "../../../../components/Redux/Switch";
import { ReduxTextField } from "../../../../components/Redux/TextField";
import SettingSection from "../../../../components/SettingSection";
import useFeatureFlag from "../../../../hooks/use-feature-flag";
import {
  getSettingsFormProperty,
  setSettingsFormError,
  setSettingsFormProperty,
  setStringSettingsFormProperty,
} from "../../../../slices/settings";
import { useDispatch, useSelector } from "../../../../store";
import { CircuitBreakerThresholds } from "../../../../store/pomeriumApi";
import { PickerType } from "../../../../types/KeyPairPicker";
import ipPort from "../../../../validators/ipPort";
import optional from "../../../../validators/optional";
import { SectionProps } from "../../../routes/components/edit_sections/common";
import CodecTypeOptions from "../CodecTypeOptions";

export const Advanced = ({
  readonly,
  tabValue,
  refresh,
}: SectionProps): ReactElement => {
  const dispatch = useDispatch();
  const circuitBreakerThresholds = useSelector(
    (state) => state.settings.form.circuitBreakerThresholds,
  );
  const idpTokensEnabled = useFeatureFlag("idp-tokens", false);

  const handleChangeCircuitBreakerThresholds = (
    value?: CircuitBreakerThresholds,
  ) => {
    dispatch(
      setSettingsFormProperty({
        path: "circuitBreakerThresholds",
        value: value,
      }),
    );
  };

  return (
    <Container component={Paper} maxWidth={false} disableGutters={true}>
      <Stack key={tabValue + refresh} spacing={1} sx={{ m: 3 }}>
        <Typography id={tabValue} variant="h2" sx={{ pt: 2 }}>
          Advanced
        </Typography>
        <Divider />
        <SettingSection label="Address" docsKey="address">
          <ReduxTextField
            disabled={readonly}
            setter={setSettingsFormProperty}
            getter={getSettingsFormProperty}
            errorHandler={setSettingsFormError}
            path="address"
            validator={ipPort}
            fullWidth
          />
        </SettingSection>
        <Divider />

        <SettingSection label="Codec Type" docsKey="codec-type">
          <BasicSelector
            path="codecType"
            options={CodecTypeOptions}
            setter={setStringSettingsFormProperty}
            getter={getSettingsFormProperty}
            errorHandler={setSettingsFormError}
            readonly={readonly}
          />
        </SettingSection>
        <Divider />

        <SettingSection
          label="HTTP Redirect Address"
          docsKey="http-redirect-address"
        >
          <ReduxTextField
            disabled={readonly}
            setter={setSettingsFormProperty}
            getter={getSettingsFormProperty}
            errorHandler={setSettingsFormError}
            path="httpRedirectAddr"
            validator={optional(ipPort)}
            fullWidth
          />
        </SettingSection>
        <Divider />

        <SettingSection
          label="Certificate Authority"
          docsKey="certificate-authority"
        >
          <KeyPairPicker
            disabled={false}
            setter={setSettingsFormProperty}
            getter={getSettingsFormProperty}
            pickerType={PickerType.CertificateAuthority}
            path="certificateAuthorityKeyPairId"
          />
        </SettingSection>
        <Divider />

        <SettingSection
          label="Downstream mTLS CA"
          docsKey="downstream-mtls-client-certificate-authority"
        >
          <KeyPairPicker
            disabled={false}
            setter={setSettingsFormProperty}
            getter={getSettingsFormProperty}
            pickerType={PickerType.CertificateAuthority}
            path="downstreamMtlsCaKeyPairId"
          />
        </SettingSection>
        <Divider />

        <SettingSection label="Secure Javascript" docsKey="javascript-security">
          <Switch
            path="cookieHttpOnly"
            label=""
            disabled={readonly}
            setter={setSettingsFormProperty}
            getter={getSettingsFormProperty}
            errorHandler={setSettingsFormError}
          />
        </SettingSection>

        {idpTokensEnabled && (
          <>
            <Divider />

            <SettingSection
              label="IDP Access Token Allowed Audiences"
              docsKey="idp-access-token-allowed-audiences"
            >
              <OptionalStringList
                path="idpAccessTokenAllowedAudiences"
                disabled={readonly}
                setter={setSettingsFormProperty}
                getter={getSettingsFormProperty}
                errorHandler={setSettingsFormError}
              />
            </SettingSection>
          </>
        )}

        <Divider />

        <SettingSection
          label="Auto-apply changesets"
          docsKey="auto-apply-changesets"
        >
          <Switch
            path="autoApplyChangesets"
            label=""
            disabled={readonly}
            setter={setSettingsFormProperty}
            getter={getSettingsFormProperty}
            errorHandler={setSettingsFormError}
          />
        </SettingSection>

        <Divider />

        <CircuitBreakerThresholdsSetting
          value={circuitBreakerThresholds}
          onChange={handleChangeCircuitBreakerThresholds}
        />

        <Divider />

        <SettingSection label="Allow Upgrades" docsKey="allow-upgrades">
          <OptionalStringList
            path="allowUpgrades"
            disabled={readonly}
            setter={setSettingsFormProperty}
            getter={getSettingsFormProperty}
            errorHandler={setSettingsFormError}
          />
        </SettingSection>
      </Stack>
    </Container>
  );
};
